<LayoutMain class="create-personal">
  <PersonalsTopBar :showCreateCTA="false" />

  <PageHeader :title="t('personals.createAd')" :subtitle="t('personals.createSubtitle')">
    <template #actions>
      <button variant="ghost" size="sm" @click="handleCancel">
        <ArrowLeft :size="16" />
        {{ t('common.actions.cancel') }}
      </button>
    </template>
  </PageHeader>

  <div class="space-y-6">
    <!-- Status Messages -->
    <div v-if="submitMessage" :class="submitSuccess ? 'success-message' : 'error-message'">
      {{ submitMessage }}
    </div>

    <!-- Ads Limit Warning -->
    <div v-if="!canCreate" class="warning-box">
      <div class="flex items-start gap-2">
        <AlertTriangle :size="16" class="mt-0.5 flex-shrink-0" />
        <div>
          <Text variant="body" class="font-medium"> {{ t('personals.adsLimitReached') }} </Text>
          <Text variant="caption" class="mt-1">
            {{ t('personals.adsLimitDescription', { current: activeAdsCount, max: 3 }) }}
          </Text>
        </div>
      </div>
    </div>

    <!-- Info Box -->
    <div v-else class="info-box">
      <div class="flex items-start gap-2">
        <Info :size="16" class="mt-0.5 flex-shrink-0" />
        <div>
          <Text variant="body" class="font-medium"> {{ t('personals.createInfo') }} </Text>
          <Text variant="caption" class="mt-1"> {{ t('personals.privacyReminder') }} </Text>
        </div>
      </div>
    </div>

    <!-- Create Form -->
    <Card variant="elevated" v-if="canCreate">
      <template #header>
        <Heading level="2" class="text-xl font-semibold text-forest-primary">
          {{ t('personals.adDetails') }}
        </Heading>
      </template>

      <div class="space-y-6">
        <!-- Title Field -->
        <div>
          <label class="block text-sm font-medium text-forest-primary mb-2">
            {{ t('personals.formTitle') }}
            <span class="text-accent-coral">*</span>
          </label>

          <input
            v-model="form.title"
            :placeholder="t('personals.titlePlaceholder')"
            :error="errors.title"
            :disabled="form.isSubmitting"
            @input="validateForm"
            class="w-full"
          />

          <div class="flex items-center justify-between mt-2">
            <Text
              variant="caption"
              :class="{
                'character-counter': true,
                'warning': titleCharacterCount > 80,
                'danger': titleCharacterCount > 100
              }"
            >
              {{ titleCharacterCount }} / 100 {{ t('common.characters') }}
            </Text>

            <div class="text-red-500" v-if="errors.title">
              <Text variant="caption">{{ errors.title }}</Text>
            </div>
          </div>
        </div>

        <!-- Body Field -->
        <div>
          <label class="block text-sm font-medium text-forest-primary mb-2">
            {{ t('personals.description') }}
            <span class="text-accent-coral">*</span>
          </label>

          <textarea
            v-model="form.body"
            :placeholder="t('personals.descriptionPlaceholder')"
            :error="errors.body"
            :disabled="form.isSubmitting"
            @input="validateForm"
            rows="8"
            class="w-full"
          />

          <div class="flex items-center justify-between mt-2">
            <Text
              variant="caption"
              :class="{
                'character-counter': true,
                'warning': bodyCharacterCount > 1600,
                'danger': bodyCharacterCount > 2000
              }"
            >
              {{ bodyCharacterCount }} / 2000 {{ t('common.characters') }}
            </Text>

            <div class="text-red-500" v-if="errors.body">
              <Text variant="caption">{{ errors.body }}</Text>
            </div>
          </div>
        </div>

        <!-- Guidelines -->
        <div class="info-box">
          <Heading level="3" class="text-sm font-semibold mb-2">
            {{ t('personals.guidelines') }}
          </Heading>

          <ul class="text-sm space-y-1">
            <li>• {{ t('personals.guideline1') }}</li>
            <li>• {{ t('personals.guideline2') }}</li>
            <li>• {{ t('personals.guideline3') }}</li>
            <li>• {{ t('personals.guideline4') }}</li>
          </ul>
        </div>
      </div>
    </Card>

    <!-- Alternative Actions for Max Ads -->
    <Card variant="elevated" v-else>
      <div class="text-center py-6">
        <Heading level="3" class="text-lg font-semibold text-forest-primary mb-2">
          {{ t('personals.manageExisting') }}
        </Heading>

        <Text variant="body" class="text-neutral-600 mb-4">
          {{ t('personals.manageExistingDescription') }}
        </Text>

        <button variant="primary" @click="$router.push({ name: 'my-personals' })">
          {{ t('personals.viewMyAds') }}
        </button>
      </div>
    </Card>
  </div>

  <!-- Form Actions (Sticky Bottom) -->
  <div v-if="canCreate" class="form-actions">
    <div class="flex justify-end gap-3">
      <button variant="outline" @click="handleCancel" :disabled="form.isSubmitting">
        {{ t('common.actions.cancel') }}
      </button>

      <button
        variant="primary"
        @click="handleSubmit"
        :disabled="form.isSubmitting || !form.title.trim() || !form.body.trim()"
      >
        <LoadingSpinner v-if="form.isSubmitting" size="sm" class="mr-2" />
        <Save v-else :size="16" class="mr-2" />
        {{ form.isSubmitting ? t('common.actions.creating') : t('common.actions.create') }}
      </button>
    </div>
  </div>
</LayoutMain>
